import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import {
  ArrowLeft, Phone, MessageCircle, Send, FileText, Share2,
  Package, Loader2, ChevronRight, Copy, Check,
} from "lucide-react";
import { BRAND, CALL_SALES, WHATSAPP_LINK } from "../lib/constants";
import { absoluteUrl } from "../lib/adminApi";
import Seo from "../components/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const buildEnquireMsg = (p) =>
  `Hi, I'm interested in ${p.name} (Category: ${p.category}). Please share the price and details.`;
const buildAskBrochureMsg = (p) =>
  `Hi, please send me the brochure for ${p.name} (Category: ${p.category}).`;

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [related, setRelated] = useState([]);
  const [copied, setCopied] = useState(false);
  const [activeImage, setActiveImage] = useState("");

  useEffect(() => {
    setLoading(true);
    axios
      .get(`${API}/products/${id}`)
      .then(async (r) => {
        setProduct(r.data);
        const imgs = (r.data.images && r.data.images.length ? r.data.images : (r.data.image_url ? [r.data.image_url] : []));
        setActiveImage(imgs[0] || "");
        try {
          const rel = await axios.get(`${API}/products?category=${encodeURIComponent(r.data.category || "")}`);
          setRelated((rel.data.products || []).filter((p) => p.id !== r.data.id).slice(0, 6));
        } catch {}
      })
      .catch(() => setProduct(null))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-[#1C6B8A]" />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-4">
        <Package className="w-10 h-10 text-slate-300 mb-3" />
        <h1 className="font-display text-2xl font-bold text-slate-900">Product not found</h1>
        <p className="text-slate-500 mt-2">It may have been removed.</p>
        <Link to="/products" className="mt-6 inline-flex items-center gap-2 h-11 px-5 rounded-full brand-btn text-sm font-semibold">
          <ArrowLeft className="w-4 h-4" /> Back to all products
        </Link>
      </div>
    );
  }

  const pdfAbs = product.pdf_url ? absoluteUrl(product.pdf_url) : "";
  const pageUrl = `${window.location.origin}/products/${product.id}`;

  const shareBrochureText = [
    `*${product.name}*`,
    product.subcategory ? `${product.category} · ${product.subcategory}` : product.category,
    "",
    product.description ? product.description.split("\n\n")[0] : "",
    pdfAbs ? `\nBrochure PDF: ${pdfAbs}` : `\nDetails: ${pageUrl}`,
    `\nHayat Security & Surveillance · +91 ${BRAND.sales}`,
  ].filter(Boolean).join("\n");
  // Opens WhatsApp with no preset number → user picks the contact and the PDF
  // link is prefilled in the message.
  const shareBrochureWaUrl = `https://api.whatsapp.com/send?text=${encodeURIComponent(shareBrochureText)}`;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(pdfAbs || pageUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {}
  };

  return (
    <div data-testid="product-detail-page" className="bg-white">
      <Seo
        title={`${product.name} — ${product.category}${product.subcategory ? " · " + product.subcategory : ""} in Nagpur`}
        description={`${(product.description || "").split("\n")[0] || `${product.name} — installed and serviced by Hayat Security across Nagpur.`} Get pricing on call/WhatsApp.`}
        canonicalPath={`/products/${product.id}`}
        image={product.image_url ? absoluteUrl(product.image_url) : undefined}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Product",
          name: product.name,
          category: product.category,
          description: product.description || `${product.name} installed by Hayat Security in Nagpur.`,
          image: product.images && product.images.length
            ? product.images.map((u) => absoluteUrl(u))
            : (product.image_url ? [absoluteUrl(product.image_url)] : []),
          brand: { "@type": "Brand", name: product.subcategory || product.category },
          offers: {
            "@type": "Offer",
            availability: "https://schema.org/InStock",
            priceCurrency: "INR",
            url: `https://www.hayatcctv.com/products/${product.id}`,
            seller: { "@id": "https://www.hayatcctv.com/#business" }
          }
        }}
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
        <nav className="flex items-center gap-1.5 text-xs text-slate-500" aria-label="Breadcrumb">
          <Link to="/" className="hover:text-[#1C6B8A]">Home</Link>
          <ChevronRight className="w-3 h-3" />
          <Link to="/products" className="hover:text-[#1C6B8A]">Products</Link>
          <ChevronRight className="w-3 h-3" />
          <span className="text-slate-900 font-medium truncate">{product.name}</span>
        </nav>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12 grid lg:grid-cols-12 gap-8 lg:gap-12">
        {/* Left: image gallery + PDF preview */}
        <div className="lg:col-span-7 space-y-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="relative aspect-[4/3] bg-white rounded-3xl overflow-hidden border border-slate-200"
          >
            {activeImage ? (
              <img
                src={absoluteUrl(activeImage)}
                alt={product.name}
                className="w-full h-full object-contain p-6"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-slate-300">
                <Package className="w-16 h-16" />
              </div>
            )}
            <span className="absolute top-4 left-4 bg-[#E8F4F8] text-[#1C6B8A] text-[10px] font-semibold uppercase tracking-wider px-2.5 py-1 rounded-full border border-[#1C6B8A]/20">
              {product.category}{product.subcategory ? ` · ${product.subcategory}` : ""}
            </span>
          </motion.div>

          {product.images && product.images.length > 1 && (
            <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1 no-scrollbar" data-testid="pd-thumbs">
              {product.images.map((url) => {
                const isActive = activeImage === url;
                return (
                  <button
                    key={url}
                    type="button"
                    onClick={() => setActiveImage(url)}
                    data-testid={`pd-thumb-${url.split("/").pop()}`}
                    className={`shrink-0 w-20 h-20 md:w-24 md:h-24 rounded-xl overflow-hidden border-2 bg-white transition-all ${
                      isActive
                        ? "border-[#1C6B8A] ring-2 ring-[#1C6B8A]/20"
                        : "border-slate-200 hover:border-slate-400"
                    }`}
                  >
                    <img
                      src={absoluteUrl(url)}
                      alt=""
                      className="w-full h-full object-contain p-1.5"
                    />
                  </button>
                );
              })}
            </div>
          )}

          {product.pdf_url && (
            <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
              <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                  <FileText className="w-4 h-4 text-[#1C6B8A]" /> Brochure preview
                </div>
                <a
                  href={absoluteUrl(product.pdf_url)}
                  target="_blank"
                  rel="noreferrer"
                  className="text-xs font-semibold text-[#1C6B8A] hover:underline"
                >
                  Open in new tab
                </a>
              </div>
              <iframe
                title="Brochure"
                src={absoluteUrl(product.pdf_url)}
                className="w-full h-[480px] bg-slate-50"
              />
            </div>
          )}
        </div>

        {/* Right: details + actions */}
        <div className="lg:col-span-5">
          <div className="lg:sticky lg:top-24">
            <div className="text-xs font-semibold text-[#1C6B8A] uppercase tracking-wider">
              {product.category}{product.subcategory ? ` · ${product.subcategory}` : ""}
            </div>
            <h1 className="font-display text-3xl md:text-4xl font-bold text-slate-900 tracking-tight mt-2 leading-tight">
              {product.name}
            </h1>

            {product.description && (
              <div className="mt-5 text-slate-600 leading-relaxed text-sm md:text-base whitespace-pre-line">
                {product.description}
              </div>
            )}

            {/* Action stack */}
            <div className="mt-7 space-y-2">
              <a
                href={CALL_SALES}
                data-testid="pd-call"
                className="w-full h-12 rounded-full brand-btn font-semibold inline-flex items-center justify-center gap-2"
              >
                <Phone className="w-4 h-4" /> Call sales · {BRAND.salesDisplay}
              </a>
              <a
                href={`https://wa.me/91${BRAND.sales}?text=${encodeURIComponent(buildEnquireMsg(product))}`}
                target="_blank"
                rel="noreferrer"
                data-testid="pd-whatsapp"
                className="w-full h-12 rounded-full bg-[#25D366] hover:bg-[#1eb355] text-white font-semibold inline-flex items-center justify-center gap-2 transition-colors"
              >
                <MessageCircle className="w-4 h-4" /> WhatsApp enquiry
              </a>
              <a
                href={shareBrochureWaUrl}
                target="_blank"
                rel="noreferrer"
                data-testid="pd-share-brochure"
                className="w-full h-12 rounded-full bg-slate-900 hover:bg-slate-800 text-white font-semibold inline-flex items-center justify-center gap-2 transition-colors"
              >
                <Share2 className="w-4 h-4" />
                {product.pdf_url ? "Send brochure on WhatsApp" : "Share product on WhatsApp"}
              </a>
              <a
                href={`https://wa.me/91${BRAND.sales}?text=${encodeURIComponent(buildAskBrochureMsg(product))}`}
                target="_blank"
                rel="noreferrer"
                data-testid="pd-ask-brochure"
                className="w-full h-12 rounded-full border border-[#1C6B8A] text-[#1C6B8A] hover:bg-[#E8F4F8] font-semibold inline-flex items-center justify-center gap-2 transition-colors"
              >
                <Send className="w-4 h-4" /> Ask brochure from Hayat
              </a>
              {product.pdf_url && (
                <a
                  href={absoluteUrl(product.pdf_url)}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="pd-download"
                  className="w-full h-12 rounded-full border border-slate-200 hover:border-slate-400 text-slate-700 font-semibold inline-flex items-center justify-center gap-2 transition-colors"
                >
                  <FileText className="w-4 h-4" /> Download PDF
                </a>
              )}
              <button
                type="button"
                onClick={copyLink}
                data-testid="pd-copy"
                className="w-full h-11 rounded-full text-slate-500 hover:text-slate-800 text-xs font-semibold inline-flex items-center justify-center gap-2"
              >
                {copied ? <Check className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4" />}
                {copied ? "Link copied" : (product.pdf_url ? "Copy brochure link" : "Copy product link")}
              </button>
            </div>

            <p className="text-[11px] text-slate-400 mt-4 text-center">
              Pricing & stock availability shared on call/WhatsApp.
            </p>
          </div>
        </div>
      </div>

      {/* Related products */}
      {related.length > 0 && (
        <section className="border-t border-slate-100 bg-slate-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
            <div className="flex items-end justify-between mb-6">
              <h2 className="font-display text-2xl md:text-3xl font-bold text-slate-900">More in {product.category}</h2>
              <Link to="/products" className="text-sm font-semibold text-[#1C6B8A] hover:underline">View all</Link>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
              {related.map((p) => (
                <button
                  key={p.id}
                  onClick={() => navigate(`/products/${p.id}`)}
                  className="group bg-white rounded-2xl border border-slate-200 overflow-hidden text-left hover:-translate-y-0.5 transition-all card-soft"
                >
                  <div className="relative aspect-square bg-white overflow-hidden border-b border-slate-100">
                    {p.image_url ? (
                      <img src={absoluteUrl(p.image_url)} alt={p.name} className="w-full h-full object-contain p-3" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-slate-300"><Package className="w-8 h-8" /></div>
                    )}
                  </div>
                  <div className="p-3">
                    <div className="text-[10px] font-semibold uppercase tracking-wider text-[#1C6B8A]">
                      {p.subcategory || p.category}
                    </div>
                    <div className="text-xs font-semibold text-slate-900 line-clamp-2 mt-0.5">{p.name}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
